import { Platform } from '@/constants';
import { dateFormatInUserTimeZone } from '@/helpers';
import { parseBotIdList, toBotRangeValue } from '@/helpers/bet';
import { AbstractModel } from '@/models/abstractModel';
import type { IBotApi } from '@/types/bot';

const BOT_DATE_FORMAT = 'dd-MMM yyyy';

export class BotItem extends AbstractModel {
    public balance: string;
    public betRepetitionId: number;
    public betSelectionId: number;
    public betTypeId: number;
    public createdAt: string;
    public currencyId: number;
    public customLeagueAgeCategoryIds: number[] | null;
    public customLeagueCategoryIds: number[] | null;
    public customLeagueGender: number | null;
    public customLeagueLimitsCategoryIds: number[] | null;
    public dballCloseGoalLineFrom: string | null;
    public dballCloseGoalLineTo: string | null;
    public dballCloseHandicapFrom: string | null;
    public dballCloseHandicapTo: string | null;
    public dballJollywhere: number | null;
    public dballJollyWinPercFrom: number | null;
    public dballJollyWinPercTo: number | null;
    public dballOpenGoalLineFrom: string | null;
    public dballOpenGoalLineTo: string | null;
    public dballOpenHandicapFrom: string | null;
    public dballOpenHandicapTo: string | null;
    public description: string | null;
    public id: number;
    public isActive: 0 | 1;
    public isBetHalfTime: 0 | 1 | null;
    public isSkipBetfairOnly: 0 | 1 | null;
    public isTopUps: 0 | 1 | null;
    public isUseMinStake: 0 | 1 | null;
    public lastActivityAt: string | null;
    public leagueGroupExcludeIds: number[] | null;
    public leagueGroupIds: number[] | null;
    public live1x2MarginPercTo: string | null;
    public liveAttackFrom: string | null;
    public liveAttackTeamSelectionId: number | null;
    public liveAttackTo: string | null;
    public liveCornersFrom: string | null;
    public liveCornersTeamSelectionId: number | null;
    public liveCornersTo: string | null;
    public liveDangerousAttackFrom: string | null;
    public liveDangerousAttackTeamSelectionId: number | null;
    public liveDangerousAttackTo: string | null;
    public liveGameSituationId: number | null;
    public liveGoalHandicapFrom: string | null;
    public liveGoalHandicapTo: string | null;
    public liveGoalLineFrom: string | null;
    public liveGoalLineTo: string | null;
    public liveGoalMarginPercTo: string | null;
    public liveHandicapFrom: string | null;
    public liveHandicapMarginPercTo: string | null;
    public liveHandicapTo: string | null;
    public liveMinuteFrom: string | null;
    public liveMinuteTo: string | null;
    public liveOddsFrom: string | null;
    public liveOddsTo: string | null;
    public livePossessionFrom: string | null;
    public livePossessionTeamSelectionId: number | null;
    public livePossessionTo: string | null;
    public liveRedCardId: number | null;
    public liveScore: string | null;
    public liveScoreSum: number | null;
    public liveScoreSumCondition: string | null;
    public liveShotsFrom: string | null;
    public liveShotsOnTargetFrom: string | null;
    public liveShotsOnTargetTeamSelectionId: number | null;
    public liveShotsOnTargetTo: string | null;
    public liveShotsTeamSelectionId: number | null;
    public liveShotsTo: string | null;
    public liveTotalAttackFrom: string | null;
    public liveTotalAttackTo: string | null;
    public liveTotalCornersFrom: string | null;
    public liveTotalCornersTo: string | null;
    public liveTotalDangerousAttackFrom: string | null;
    public liveTotalDangerousAttackTo: string | null;
    public liveTotalShotsFrom: string | null;
    public liveTotalShotsOnTargetFrom: string | null;
    public liveTotalShotsOnTargetTo: string | null;
    public liveTotalShotsTo: string | null;
    public name: string;
    public periodTypeId: number;
    public platformId: number;
    public stake: string | null;
    public toolUserId: number;
    public toolUserLogin: string;
    public totalBetCount: number;
    public totalStakeSum: string;

    constructor(data: IBotApi) {
        super();

        this.id = data.id;
        this.name = data.name;
        this.description = data.description;
        this.isActive = data.is_active;
        this.periodTypeId = data.period_type_id;
        this.toolUserId = data.tool_user_id;
        this.toolUserLogin = data.tool_user_login;
        this.platformId = data.user_platform_id;
        this.currencyId = data.tool_user_currency_id;
        this.createdAt = data.created_at;
        this.lastActivityAt = data.last_activity_at;
        this.totalBetCount = data.total_bet_count;
        this.totalStakeSum = data.total_stake_sum;
        this.balance = data.balance;
        this.stake = data.stake;
        this.isUseMinStake = data.is_use_min_stake;
        this.isTopUps = data.is_top_ups;
        this.isSkipBetfairOnly = data.is_skip_betfair_only;
        this.betTypeId = data.bet_type_id;
        this.betRepetitionId = data.bet_repetition_id;
        this.betSelectionId = data.bet_selection_id;
        this.liveRedCardId = data.live_red_card_id;
        this.isBetHalfTime = data.is_bet_half_time;
        this.liveOddsFrom = data.live_odds_from;
        this.liveOddsTo = data.live_odds_to;
        this.liveMinuteFrom = data.live_minute_from;
        this.liveMinuteTo = data.live_minute_to;
        this.liveScore = data.live_score;
        this.liveGameSituationId = data.live_game_situation_id;
        this.liveHandicapFrom = data.live_handicap_from;
        this.liveHandicapTo = data.live_handicap_to;
        this.liveGoalHandicapFrom = data.live_goal_handicap_from;
        this.liveGoalHandicapTo = data.live_goal_handicap_to;
        this.liveGoalLineFrom = data.live_goal_line_from;
        this.liveGoalLineTo = data.live_goal_line_to;
        this.liveScoreSumCondition = data.live_score_sum_condition;
        this.liveScoreSum = data.live_score_sum;
        this.liveAttackTeamSelectionId = data.live_attack_team_selection_id;
        this.liveAttackFrom = toBotRangeValue(data.live_attack_from);
        this.liveAttackTo = toBotRangeValue(data.live_attack_to);
        this.liveDangerousAttackTeamSelectionId = data.live_dangerous_attack_team_selection_id;
        this.liveDangerousAttackFrom = toBotRangeValue(data.live_dangerous_attack_from);
        this.liveDangerousAttackTo = toBotRangeValue(data.live_dangerous_attack_to);
        this.liveShotsTeamSelectionId = data.live_shots_team_selection_id;
        this.liveShotsFrom = toBotRangeValue(data.live_shots_from);
        this.liveShotsTo = toBotRangeValue(data.live_shots_to);
        this.liveShotsOnTargetTeamSelectionId = data.live_shots_on_target_team_selection_id;
        this.liveShotsOnTargetFrom = toBotRangeValue(data.live_shots_on_target_from);
        this.liveShotsOnTargetTo = toBotRangeValue(data.live_shots_on_target_to);
        this.liveCornersTeamSelectionId = data.live_corners_team_selection_id;
        this.liveCornersFrom = toBotRangeValue(data.live_corners_from);
        this.liveCornersTo = toBotRangeValue(data.live_corners_to);
        this.livePossessionTeamSelectionId = data.live_possession_team_selection_id;
        this.livePossessionFrom = toBotRangeValue(data.live_possession_from);
        this.livePossessionTo = toBotRangeValue(data.live_possession_to);
        this.liveTotalAttackFrom = toBotRangeValue(data.live_total_attack_from);
        this.liveTotalAttackTo = toBotRangeValue(data.live_total_attack_to);
        this.liveTotalDangerousAttackFrom = toBotRangeValue(data.live_total_dangerous_attack_from);
        this.liveTotalDangerousAttackTo = toBotRangeValue(data.live_total_dangerous_attack_to);
        this.liveTotalShotsFrom = toBotRangeValue(data.live_total_shots_from);
        this.liveTotalShotsTo = toBotRangeValue(data.live_total_shots_to);
        this.liveTotalShotsOnTargetFrom = toBotRangeValue(data.live_total_shots_on_target_from);
        this.liveTotalShotsOnTargetTo = toBotRangeValue(data.live_total_shots_on_target_to);
        this.liveTotalCornersFrom = toBotRangeValue(data.live_total_corners_from);
        this.liveTotalCornersTo = toBotRangeValue(data.live_total_corners_to);
        this.live1x2MarginPercTo = data.live_1x2_margin_perc_to;
        this.liveHandicapMarginPercTo = data.live_handicap_margin_perc_to;
        this.liveGoalMarginPercTo = data.live_goal_margin_perc_to;
        this.customLeagueGender = data.custom_league_gender;
        this.customLeagueAgeCategoryIds = parseBotIdList(data.custom_league_age_category_ids);
        this.customLeagueCategoryIds = parseBotIdList(data.custom_league_category_ids);
        this.customLeagueLimitsCategoryIds = parseBotIdList(data.custom_league_limits_category_ids);
        this.leagueGroupIds = parseBotIdList(data.league_group_ids);
        this.leagueGroupExcludeIds = parseBotIdList(data.league_group_exclude_ids);
        this.dballJollywhere = data.dball_jollywhere;
        this.dballJollyWinPercFrom = data.dball_jolly_win_perc_from;
        this.dballJollyWinPercTo = data.dball_jolly_win_perc_to;
        this.dballOpenGoalLineFrom = data.dball_open_goal_line_from;
        this.dballOpenGoalLineTo = data.dball_open_goal_line_to;
        this.dballOpenHandicapFrom = data.dball_open_handicap_from;
        this.dballOpenHandicapTo = data.dball_open_handicap_to;
        this.dballCloseGoalLineFrom = data.dball_close_goal_line_from;
        this.dballCloseGoalLineTo = data.dball_close_goal_line_to;
        this.dballCloseHandicapFrom = data.dball_close_handicap_from;
        this.dballCloseHandicapTo = data.dball_close_handicap_to;
    }

    get formatCreatedDate(): string {
        return this.createdAt ? dateFormatInUserTimeZone(this.createdAt, BOT_DATE_FORMAT) : '';
    }

    get formatLastActivityDate(): string {
        return this.lastActivityAt ? dateFormatInUserTimeZone(this.lastActivityAt, BOT_DATE_FORMAT) : '';
    }

    get isTradeballUser(): boolean {
        return this.platformId === Platform.Tradeball;
    }
}
